import React, { useState, useEffect } from 'react';
import './Configuration.css';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  Snackbar
} from '@mui/material';
import { getBackendUrl } from '../../../api.js';

const SystemConfiguration = () => {
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [pendingReasonFor, setPendingReasonFor] = useState(null);
  const [pendingReasonText, setPendingReasonText] = useState('');
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [globalPopup, setGlobalPopup] = useState({ open: false, id: null, detail: null, notificationId: null });

  const getCurrentYearDefault = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const loadHolidays = async () => {
      try {
        const backendUrl = getBackendUrl();
        const res = await fetch(`${backendUrl}/api/holidays`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            setSelectedDates(data.map(h => ({ id: h.id, date: h.date, reason: h.reason || null })));
          }
        }
      } catch (e) {
        console.warn('Failed to load holidays from backend', e);
      }
    };
    loadHolidays();
  }, []);

  const handleCloseAlert = () => {
    setShowAlert(false);
    setError('');
    setSuccessMessage('');
  };

  const openCalendar = () => {
    setDateInput(getCurrentYearDefault());
    setPendingReasonFor(null);
    setPendingReasonText('');
    setShowCalendarModal(true);
  };

  const closeCalendar = () => {
    setShowCalendarModal(false);
    setPendingReasonFor(null);
    setPendingReasonText('');
    setDateInput('');
    setEditingHoliday(null);
  };

  const submitReasonForDate = async () => {
    // Validate date
    if (!dateInput) {
      setError('Please select a date');
      setSuccessMessage('');
      setShowAlert(true);
      return;
    }
  
    // Validate reason - MANDATORY field
    if (!pendingReasonText || !pendingReasonText.trim()) {
      setError('Reason for Holiday is mandatory. Please provide a reason.');
      setSuccessMessage('');
      setShowAlert(true);
      return;
    }
  
    const payload = { date: dateInput, reason: pendingReasonText.trim() };
  
    try {
      const backendUrl = getBackendUrl();
      const res = await fetch(`${backendUrl}/api/holidays`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
  
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save holiday');
  
      const saved = data.holiday || data || { id: null, date: payload.date, reason: payload.reason };
      
      setSelectedDates(prev => {
        const exists = prev.find(p => p.id && saved.id ? p.id === saved.id : p.date === saved.date);
        if (exists) {
          return prev.map(p => ((p.id && saved.id ? p.id === saved.id : p.date === saved.date) ? { ...p, ...saved } : p));
        }
        return [...prev, saved];
      });
  
      setPendingReasonFor(null);
      setPendingReasonText('');
      setDateInput('');
      setShowCalendarModal(false);
      setEditingHoliday(null);
      setSuccessMessage('Holiday saved successfully');
      setError('');
      setShowAlert(true);
    } catch (err) {
      setError(err.message || 'Failed to save holiday');
      setSuccessMessage('');
      setShowAlert(true);
    }
  };

  const editHoliday = (holiday) => {
    const date = holiday.date;
    const found = selectedDates.find(s => s.date === date || (s.id && holiday.id && s.id === holiday.id));
    if (found) {
      setEditingHoliday(holiday);
      setDateInput(date);
      setPendingReasonFor(date);
      setPendingReasonText(found.reason || '');
      setShowCalendarModal(true);
    }
  };

  const deleteHoliday = async (holiday) => {
    if (!holiday) return;
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete the holiday on ${holiday.date}?`)) {
      return;
    }
    
    const previous = selectedDates;
    setSelectedDates(prev => prev.filter(s => (holiday.id ? s.id !== holiday.id : s.date !== holiday.date)));

    try {
      const backendUrl = getBackendUrl();
      const payload = holiday.id ? { id: holiday.id } : { date: holiday.date };
      const res = await fetch(`${backendUrl}/api/holidays`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setSelectedDates(previous);
        setError(data.error || 'Failed to delete holiday');
        setSuccessMessage('');
        setShowAlert(true);
        return;
      }
      
      setSuccessMessage('Holiday deleted successfully');
      setError('');
      setShowAlert(true);
    } catch (e) {
      setSelectedDates(previous);
      setError('Failed to delete holiday: ' + e.message);
      setSuccessMessage('');
      setShowAlert(true);
    }
  };

  return (
    <Box className="system-config-wrapper">
      <div className="main-title">Holiday Calendar Management</div>

      <Card className="card-section holiday">
        <CardContent>
          <Box className="holiday-header">
            <Typography variant="h6">Holiday Calendar</Typography>
            <Button variant="contained" className="add-holiday-btn" onClick={openCalendar}>+ Add Holiday</Button>
          </Box>

          <Box className="holiday-placeholder">
            {selectedDates.length === 0 ? (
              <>
                <Typography className="calendar-icon">📅</Typography>
                <Typography className="calendar-text">No holidays added yet</Typography>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedDates.map(s => (
                  <Box key={s.date + (s.id || '')} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, padding: '6px 8px', borderRadius: 1, background: '#fafafa' }}>
                    <Box>
                      <Typography variant="body2"><strong>{s.date}</strong></Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>{s.reason || 'No reason provided'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => editHoliday(s)}>Edit</Button>
                      <Button size="small" variant="text" color="error" onClick={() => deleteHoliday(s)}>Delete</Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {showCalendarModal && (
            <div className="calendar-modal-overlay">
              <div className="calendar-modal">
                <Typography variant="h6">{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</Typography>
                <Typography variant="caption">Select a date and provide a reason for the holiday</Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Date</Typography>
                  <TextField 
                    type="date" 
                    value={dateInput}
                    onChange={(e) => setDateInput(e.target.value)}
                    size="small" 
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Reason for Holiday <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField 
                    fullWidth 
                    multiline 
                    minRows={3} 
                    value={pendingReasonText}
                    onChange={(e) => setPendingReasonText(e.target.value)}
                    placeholder="Enter reason for marking this date as a holiday (required)"
                    required
                    error={!pendingReasonText.trim() && pendingReasonFor !== null}
                    helperText={!pendingReasonText.trim() && pendingReasonFor !== null ? "This field is required" : ""}
                  />
                </Box>

                <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button variant="text" onClick={closeCalendar}>Cancel</Button>
                  <Button 
                    variant="contained" 
                    onClick={submitReasonForDate}
                    disabled={!dateInput || !pendingReasonText.trim()}
                  >
                    Submit
                  </Button>
                </Box>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Snackbar open={showAlert} autoHideDuration={6000} onClose={handleCloseAlert} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseAlert} severity={error ? 'error' : 'success'} variant="filled">
          {error || successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemConfiguration;